<template>
    <div class="h-full w-full flex flex-col">
        <div class="h-full container mx-auto">
            <div class="px-8 flex flex-col h-full w-full py-8 space-y-8">
                <!-- Breadcrumb and Title -->
                <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
                    <div class="flex items-center text-sm font-light flex-wrap">
                        <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
                        <span class="mx-2">/</span>
                        <router-link to="/work-readiness-program" class="opacity-50 hover:opacity-100">Work Readiness
                            Program</router-link>
                        <span class="mx-2">/</span>
                        <span>{{ activity.title || '...' }}</span>
                    </div>
                    <div class="font-bold mt-2 text-xl">{{ activity.title }}</div>
                </div>

                <!-- Activity Card -->
                <div class="bg-white rounded-2xl shadow p-6 sm:p-8 space-y-4">
                    <h1 class="text-2xl font-bold text-indigo-700">{{ activity.title }}</h1>
                    <p class="text-gray-600 text-sm">{{ activity.description }}</p>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-500">
                        <div><strong>Points:</strong> {{ activity.points }}</div>
                        <div class="capitalize"><strong>Valuation Type:</strong> {{ activity.valuation_type }}</div>
                        <div v-if="activity.is_external && activity.external_link">
                            <strong>Registration:</strong> External link
                            <a :href="activity.external_link" target="_blank" rel="noopener noreferrer"
                                class="ml-2 text-indigo-600 hover:underline">{{ activity.external_link }}</a>
                        </div>
                        <div><strong>Start Date:</strong> {{ formatDate(activity.start_date) }}</div>
                        <div><strong>End Date:</strong> {{ formatDate(activity.end_date) }}</div>
                    </div>
                </div>

                <!-- External Link Tracking (for external activities) -->
                <div v-if="activity.is_external && activity.external_link"
                    class="bg-white rounded-2xl shadow p-6 sm:p-8 space-y-4">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <h2 class="text-xl font-semibold text-gray-800">External Registration Tracking</h2>
                        <button
                            v-if="hasPermission('work-readiness-submission-view') && submissions.length === 0"
                            type="button"
                            class="wrp-export-btn"
                            :disabled="isExportExcel"
                            @click="generateReport"
                        >
                            <font-awesome-icon
                                v-if="isExportExcel"
                                :icon="['fas', 'spinner']"
                                spin
                                class="wrp-export-btn-icon"
                            />
                            <font-awesome-icon
                                v-else
                                :icon="['fas', 'file-excel']"
                                class="wrp-export-btn-icon"
                            />
                            <span>{{ isExportExcel ? 'Exporting Excel...' : 'Export Excel Report' }}</span>
                        </button>
                    </div>
                    <div class="flex gap-2 mb-4">
                        <button @click="fetchLinkTracks('all')"
                            :class="['px-4 py-2 rounded text-sm font-medium', linkTrackFilter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300']">
                            All
                        </button>
                        <button @click="fetchLinkTracks('clicked')"
                            :class="['px-4 py-2 rounded text-sm font-medium', linkTrackFilter === 'clicked' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300']">
                            Clicked Link
                        </button>
                        <button @click="fetchLinkTracks('registered')"
                            :class="['px-4 py-2 rounded text-sm font-medium', linkTrackFilter === 'registered' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300']">
                            Confirmed Registration
                        </button>
                    </div>
                    <table class="min-w-full table-auto border" v-if="linkTracksData && linkTracksData.length > 0">
                        <thead class="bg-gray-100 text-sm text-gray-600 uppercase">
                            <tr>
                                <th class="px-4 py-2 text-left">#</th>
                                <th class="px-4 py-2 text-left">User Name</th>
                                <th class="px-4 py-2 text-left">Email</th>
                                <th class="px-4 py-2 text-left">Action</th>
                                <th class="px-4 py-2 text-left">Date & Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(track, index) in linkTracksData" :key="track.id"
                                class="border-t hover:bg-gray-50">
                                <td class="px-4 py-2">{{ index + 1 }}</td>
                                <td class="px-4 py-2">{{ track.user && track.user.name }}</td>
                                <td class="px-4 py-2">{{ track.user && track.user.email }}</td>
                                <td class="px-4 py-2">
                                    <span
                                        :class="['px-2 py-1 rounded text-xs font-medium', track.action === 'registered' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700']">
                                        {{ track.action === 'registered' ? 'Registered' : 'Clicked' }}
                                    </span>
                                </td>
                                <td class="px-4 py-2">{{ formatDateTime(track.created_at) }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p v-else-if="linkTracksData && linkTracksData.length === 0" class="text-gray-500 text-sm py-4">
                        No link tracking records yet.
                    </p>
                </div>

                <div v-if="errorMsg" class="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                    {{ errorMsg }}
                </div>

                <!-- Submissions List -->
                <div class="bg-white rounded-2xl shadow p-6 sm:p-8 space-y-4" v-if="!isLoading && submissions.length > 0">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <h2 class="text-xl font-semibold text-gray-800">Submitted Students</h2>
                        <button
                            v-if="hasPermission('work-readiness-submission-view')"
                            type="button"
                            class="wrp-export-btn"
                            :disabled="isExportExcel"
                            @click="generateReport"
                        >
                            <font-awesome-icon
                                v-if="isExportExcel"
                                :icon="['fas', 'spinner']"
                                spin
                                class="wrp-export-btn-icon"
                            />
                            <font-awesome-icon
                                v-else
                                :icon="['fas', 'file-excel']"
                                class="wrp-export-btn-icon"
                            />
                            <span>{{ isExportExcel ? 'Exporting Excel...' : 'Export Excel Report' }}</span>
                        </button>
                    </div>
                    <table class="min-w-full table-auto border">
                        <thead class="bg-gray-100 text-sm text-gray-600 uppercase">
                            <tr>
                                <th class="px-4 py-2 text-left">#</th>
                                <th class="px-4 py-2 text-left">Student Name</th>
                                <th class="px-4 py-2 text-left">Submission Note</th>
                                <th class="px-4 py-2 text-left">Documents</th>
                                <th class="px-4 py-2 text-left">Submission Date</th>
                                <th class="px-4 py-2 text-left">Status</th>
                                <th class="px-4 py-2 text-left">Points</th>
                                <th class="px-4 py-2 text-left">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(submission, index) in submissions" :key="submission.id"
                                class="border-t hover:bg-gray-50">
                                <td class="px-4 py-2">{{ paginatorInfo.firstItem + index }}</td>
                                <td class="px-4 py-2">
                                    <div class="flex flex-col">
                                        <div class="text-sm">{{ submission.user.name }}</div>
                                        <div class="text-xs text-gray-400">{{ submission.user.email }}</div>
                                    </div>
                                    <!-- <div class="flex items-center">
                                        <div class="flex-shrink-0 mr-3">
                                            <img class="h-12 w-12 rounded-lg object-cover cursor-pointer" :src="getProfilePicture(
                                                submission.user.students.profile_picture_url,
                                                submission.user.students.student_id,
                                                submission.user.students.entity
                                            )
                                                " alt="" />
                                        </div>
                                        <div class="flex-grow flex flex-col">
                                            <h1 class="text-sm font-semibold flex items-center">
                                                <span @click.stop="
                                                    viewStudentProfile(submission.user.students.student_id)
                                                    "
                                                    class="text-heading hover:text-blue-600 hover:underline cursor-pointer">{{
                                                        submission.user.name_en }}</span>
                                            </h1>
                                            <p class="text-xs text-gray-400">
                                                {{ submission.user.students.name_ar }}
                                            </p>
                                            <p class="text-xs text-gray-400">
                                                {{ submission.user.students.student_id }}
                                            </p>
                                        </div>
                                    </div> -->

                                </td>
                                <td class="px-4 py-2">
                                    <div class="wrp-note-cell">
                                        <div
                                            v-if="getStudentLastMessageText(submission) !== '—'"
                                            class="wrp-note-preview"
                                        >
                                            <p class="wrp-note-text">{{ truncateMessage(getStudentLastMessageText(submission), 120) }}</p>
                                        </div>
                                        <p v-else class="wrp-note-empty">No message yet</p>
                                        <button type="button" class="wrp-chat-btn" @click="openChat(submission)">
                                            <span class="wrp-chat-btn-icon-wrap">
                                                <font-awesome-icon :icon="['fas', 'comments']" class="wrp-chat-btn-icon" />
                                            </span>
                                            <span class="wrp-chat-btn-label">View &amp; Reply</span>
                                        </button>
                                    </div>
                                </td>
                                <td class="px-4 py-2">
                                    <!-- <div v-if="submission.attachments.length">
                                        {{ getDocuments(submission.attachments) }}
                                    </div>
                                    <div v-else>
                                        No documents uploaded
                                    </div> -->
                                    <div v-html="getDocuments(submission.attachments)"></div>
                                </td>
                                <td class="px-4 py-2">{{ formatDate(submission.created_at) }}</td>
                                <td class="px-4 py-2">{{ submission.status }}</td>
                                <td class="px-4 py-2">{{ submission.points }}</td>
                                <td class="px-4 py-2">
                                    <button
                                        v-if="hasPermission('work-readiness-program-allocate-point') && submission.status == 'pending'"
                                        @click.stop="allocatePoint(submission.id, activity.points)"
                                        class="text-sm bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600">Add
                                        Points</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="mt-4">
                        <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                    </div>
                </div>

                <div v-if="!isLoading && !errorMsg && submissions.length === 0"
                    class="bg-white rounded-2xl shadow p-6 sm:p-8 text-center text-gray-500 text-sm">
                    No submissions found.
                </div>
            </div>
        </div>

        <!-- Spinner -->
        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>

        <!-- Conversation modal (styled like Add New Contact) -->
        <transition name="wrp-fade">
            <base-modal
                v-if="chatSubmission"
                width="600px"
                content-overflow="visible"
                @close="closeChat"
            >
                <template #header>
                    <div class="wrp-modal-header">
                        <div class="wrp-modal-header-inner">
                            <div class="wrp-modal-header-icon-wrap">
                                <font-awesome-icon :icon="['fas', 'comments']" class="wrp-modal-header-icon" />
                            </div>
                            <div class="text-left">
                                <h2 class="wrp-modal-header-title">Conversation</h2>
                                <p class="wrp-modal-header-sub">
                                    {{ chatSubmission.user && chatSubmission.user.name }}
                                    <span v-if="chatSubmission.user && chatSubmission.user.email" class="wrp-modal-header-email">
                                        · {{ chatSubmission.user.email }}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </template>

                <div class="wrp-modal-body">
                    <div class="wrp-modal-card">
                        <submission-chat
                            variant="modal"
                            :submission-id="chatSubmission.id"
                            :student-name="chatSubmission.user && chatSubmission.user.name"
                        />
                    </div>
                    <p class="wrp-modal-hint">
                        <font-awesome-icon :icon="['fas', 'lightbulb']" class="wrp-modal-hint-icon" />
                        Messages are shared with the student. They will be notified when you reply.
                    </p>
                </div>
            </base-modal>
        </transition>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import xlsx from "xlsx/dist/xlsx.full.min.js";
import { getEntityName } from "../../helpers/utils.js";
import Pagination from "../../components/shared/pagination.vue";
import SubmissionChat from "../../components/work-readiness/SubmissionChat.vue";

const GET_SUBMITTED_WORK_READINESS_PROGRAM = gql`
  query GetSubmittedWorkReadinessProgram($token: String!, $page: Int!, $first: Int!) {
    getSubmittedWorkReadinessProgram(token: $token, page: $page, first: $first) {
      error
      error_code
      message
      data {
        id
        user {
          name
          email
          students {
            student_id
            entity
            profile_picture_url
            name_ar
          }
        }
        note
        status
        points
        latestStudentMessage {
          message
        }
        attachments {
            path
        }
        created_at
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

const ALLOCATE_POINTS_MUTATION = gql`
  mutation allocatePoint($id: Int!, $points: Int!) {
    allocatePoint(id: $id, points: $points) {
      error
      message
    }
  }
`;

const FETCH_WORK_READINESS_PROGRAN = gql`
    query FetchWorkReadinessProgram($token: String!) {
        fetchWorkReadinessProgram(token: $token) {
            error
            error_code
            message
            data {
                title
                description
                points
                valuation_type
                is_external
                external_link
                start_date
                end_date
            }
        }
    }
`;

const GET_EXTERNAL_LINK_TRACKS = gql`
  query GetExternalLinkTracks($token: String!, $action: String) {
    getExternalLinkTracks(token: $token, action: $action) {
      error
      error_code
      message
      data {
        id
        action
        created_at
        user {
          id
          name
          email
        }
      }
    }
  }
`;

const EXPORT_SUBMITTED_WORK_READINESS_PROGRAM = gql`
  query ExportSubmittedWorkReadinessProgram($token: String!, $page: Int!, $first: Int!) {
    getSubmittedWorkReadinessProgram(token: $token, page: $page, first: $first) {
      error
      error_code
      message
      data {
        id
        user {
          name
          email
          students {
            student_id
            entity
            campus
            campus_code
            program_name
          }
        }
        note
        status
        points
        latestStudentMessage {
          message
        }
        created_at
        updated_at
      }
      paginatorInfo {
        hasMorePages
        total
      }
    }
  }
`;

export default {
    name: "submitted-work-readiness-program",
    components: {
        Pagination,
        SubmissionChat,
    },
    props: {
        token: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            isLoading: false,
            activity: {},
            errorMsg: null,
            page: 1,
            first: 10,
            submissions: [],
            linkTrackFilter: 'all',
            linkTracksData: null,
            chatSubmission: null,
            isExportExcel: false,
            generatedReport: [],
            generatedLinkTrackReport: [],
            isApiCallRecurisive: false,
        };
    },
    created() {
        this.fetchActivityData();
        this.fetchSubmittedWorkReadinessProgram();
    },
    watch: {
        'activity.is_external'(val) {
            if (val && this.activity.external_link) {
                this.fetchLinkTracks('all');
            }
        },
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
    },
    methods: {
        openChat(submission) {
            this.chatSubmission = submission;
        },
        closeChat() {
            this.chatSubmission = null;
        },
        getStudentLastMessageText(submission) {
            if (submission.latestStudentMessage && submission.latestStudentMessage.message) {
                return submission.latestStudentMessage.message;
            }
            if (submission.note) {
                return submission.note;
            }
            return '—';
        },
        truncateMessage(text, maxLen) {
            if (!text || text === '—') {
                return text || '—';
            }
            if (text.length <= maxLen) {
                return text;
            }
            return text.substring(0, maxLen) + '...';
        },
        hasPermission(crudName) {
            if (this.role == 'superadmin') {
                return true;
            } else {
                const permissions = this.$store.getters.permissions;
                const permissionsArray = permissions.split(",");
                if (permissionsArray.includes(crudName)) {
                    return true;
                } else {
                    return false;
                }
            }
        },
        setPage(page) {
            this.page = page;
            this.fetchSubmittedWorkReadinessProgram();
        },
        async fetchActivityData() {
            this.isLoading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: FETCH_WORK_READINESS_PROGRAN,
                    variables: { token: this.token },
                    fetchPolicy: 'network-only'
                });

                const { error, message, data: program } = data.fetchWorkReadinessProgram;

                if (error) {
                    this.$swal.fire('Error', message, 'error');
                    this.$router.push('/work-readiness-program');
                    return;
                }

                this.activity = {
                    title: program.title,
                    description: program.description,
                    points: program.points,
                    valuation_type: program.valuation_type,
                    is_external: program.is_external,
                    external_link: program.external_link,
                    start_date: program.start_date,
                    end_date: program.end_date,
                };

                if (this.activity.is_external && this.activity.external_link) {
                    this.fetchLinkTracks('all');
                }
            } catch (err) {
                console.error("Failed to fetch work readiness program:", err);
            } finally {
                this.isLoading = false;
            }
        },
        async fetchLinkTracks(action) {
            this.linkTrackFilter = action;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_EXTERNAL_LINK_TRACKS,
                    variables: {
                        token: this.token,
                        action: action === 'all' ? null : action,
                    },
                    fetchPolicy: 'network-only',
                });

                if (data.getExternalLinkTracks.error) {
                    this.linkTracksData = [];
                    return;
                }

                this.linkTracksData = data.getExternalLinkTracks.data || [];
            } catch (err) {
                console.error("Failed to fetch link tracks:", err);
                this.linkTracksData = [];
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return "-";
            const options = { year: "numeric", month: "short", day: "numeric" };
            return new Date(dateStr).toLocaleDateString(undefined, options);
        },
        formatDateTime(dateStr) {
            if (!dateStr) return "-";
            const options = { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
            return new Date(dateStr).toLocaleDateString(undefined, options);
        },
        async fetchSubmittedWorkReadinessProgram() {
            try {
                this.isLoading = true;

                const { data } = await this.$apollo.query({
                    query: GET_SUBMITTED_WORK_READINESS_PROGRAM,
                    variables: {
                        token: this.token,
                        page: this.page,
                        first: this.first,
                    },
                    fetchPolicy: 'network-only',
                });

                if (data.getSubmittedWorkReadinessProgram.error) {
                    this.errorMsg = data.getSubmittedWorkReadinessProgram.message || "Failed to fetch user.";
                    return;
                }

                this.submissions = data.getSubmittedWorkReadinessProgram.data;
                this.paginatorInfo = data.getSubmittedWorkReadinessProgram.paginatorInfo;

            } catch (error) {
                this.errorMsg = error.message || "Something went wrong.";
            } finally {
                this.isLoading = false;
            }
        },
        getProfilePicture(profilePictureUrl, studentId, entity) {
            if (profilePictureUrl) {
                if (
                    profilePictureUrl ==
                    "/storage/alumni-profile-pictures/default-avatar.jpg"
                ) {
                    return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entity}`;
                } else {
                    return process.env.VUE_APP_PUBLIC_URL + profilePictureUrl;
                }
            } else {
                return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entity}`;
            }
        },
        viewStudentProfile(studentId) {
            // this.studentId = studentId;
            // this.showStudentProfileFullModal = true;
            console.log(studentId)
        },
        async allocatePoint(id, points) {
            this.isLoading = true;
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: ALLOCATE_POINTS_MUTATION,
                    variables: {
                        id: parseInt(id),
                        points: parseInt(points),
                    },
                });

                const res = data.allocatePoint;
                if (res.error) {
                    this.$swal.fire('Error', res.message, 'error');
                } else {
                    this.$swal.fire('Success', res.message, 'success');
                    this.fetchSubmittedWorkReadinessProgram();
                }
            } catch (error) {
                this.$swal.fire('Oops', error.message || 'Something went wrong', 'error');
            } finally {
                this.isLoading = false;
            }
        },
        getDocuments(attachments) {
            if (!attachments || attachments.length === 0) {
                return `<span class="text-gray-400 italic">No documents</span>`;
            }

            return attachments
                .map((doc, i) => {
                    const fullUrl = `${process.env.VUE_APP_PUBLIC_URL}${doc.path}`;
                    return `
        <div class="mb-1">
          <a href="${fullUrl}" target="_blank" 
            class="inline-flex items-center text-blue-600 hover:underline hover:text-blue-800 text-sm transition">
            <i class="fas fa-file-alt mr-1"></i>
            Document ${i + 1}
          </a>
        </div>`;
                })
                .join('');
        },
        getStudentCampusForExport(student) {
            if (!student) {
                return '';
            }
            if (student.campus) {
                return student.campus;
            }
            if (student.campus_code) {
                return student.campus_code;
            }
            return '';
        },
        buildSubmissionExportRow(submission, rowNumber) {
            var student = submission.user && submission.user.students ? submission.user.students : null;
            var latestMessage = this.getStudentLastMessageText(submission);
            if (latestMessage === '—') {
                latestMessage = '';
            }
            return {
                '#': rowNumber,
                'Student Name': submission.user && submission.user.name ? submission.user.name : '',
                'Student Email': submission.user && submission.user.email ? submission.user.email : '',
                'Student ID': student && student.student_id ? student.student_id : '',
                'Entity': student && student.entity ? getEntityName(student.entity) : '',
                'Campus': this.getStudentCampusForExport(student),
                'Program Name': student && student.program_name ? student.program_name : '',
                'Submission Note': submission.note || '',
                'Latest Student Message': latestMessage,
                'Status': submission.status || '',
                'Points Awarded': submission.points != null ? submission.points : '',
                'Submission Date': this.formatDate(submission.created_at),
                'Last Updated': this.formatDate(submission.updated_at),
            };
        },
        async fetchAllLinkTracksForExport() {
            try {
                const { data } = await this.$apollo.query({
                    query: GET_EXTERNAL_LINK_TRACKS,
                    variables: {
                        token: this.token,
                        action: null,
                    },
                    fetchPolicy: 'network-only',
                });

                if (data.getExternalLinkTracks.error) {
                    this.generatedLinkTrackReport = [];
                    return;
                }

                var tracks = data.getExternalLinkTracks.data || [];
                var self = this;
                this.generatedLinkTrackReport = tracks.map(function (track, index) {
                    return {
                        '#': index + 1,
                        'User Name': track.user && track.user.name ? track.user.name : '',
                        'Email': track.user && track.user.email ? track.user.email : '',
                        'Action': track.action === 'registered' ? 'Confirmed Registration' : 'Clicked Link',
                        'Date & Time': self.formatDateTime(track.created_at),
                    };
                });
            } catch (err) {
                console.error("Failed to fetch link tracks for export:", err);
                this.generatedLinkTrackReport = [];
            }
        },
        getExportFileName() {
            var title = this.activity.title || 'Work_Readiness_Activity';
            var safeTitle = title.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '');
            if (!safeTitle) {
                safeTitle = 'Work_Readiness_Activity';
            }
            var dateStamp = new Date().toISOString().slice(0, 10);
            return 'WRP_Submissions_' + safeTitle + '_' + dateStamp;
        },
        async fetchSubmissionsForExport(pageNo, limitSize) {
            var response = await this.$apollo.query({
                query: EXPORT_SUBMITTED_WORK_READINESS_PROGRAM,
                variables: {
                    token: this.token,
                    page: pageNo,
                    first: limitSize,
                },
                fetchPolicy: 'network-only',
            });
            return response.data.getSubmittedWorkReadinessProgram;
        },
        async generateReport() {
            if (!this.hasPermission('work-readiness-submission-view')) {
                return;
            }

            this.generatedReport = [];
            this.generatedLinkTrackReport = [];
            this.isExportExcel = true;
            this.isApiCallRecurisive = true;

            var exportPage = 1;
            var exportLimit = 200;
            var rowNumber = 1;

            try {
                if (this.activity.is_external && this.activity.external_link) {
                    await this.fetchAllLinkTracksForExport();
                }

                while (this.isApiCallRecurisive) {
                    var responseData = await this.fetchSubmissionsForExport(exportPage, exportLimit);

                    if (responseData && responseData.error) {
                        this.$swal.fire('Error', responseData.message || 'Export failed.', 'error');
                        break;
                    }

                    var rows = responseData && responseData.data ? responseData.data : [];
                    if (rows.length > 0) {
                        var self = this;
                        this.generatedReport.push(
                            ...rows.map(function (submission) {
                                var exportRow = self.buildSubmissionExportRow(submission, rowNumber);
                                rowNumber++;
                                return exportRow;
                            })
                        );
                        exportPage++;
                    } else {
                        this.isApiCallRecurisive = false;
                    }
                }

                if (this.generatedReport.length === 0 && this.generatedLinkTrackReport.length === 0) {
                    this.$swal.fire('Info', 'No records available to export.', 'info');
                    return;
                }

                this.exportToExcel(this.getExportFileName());
            } catch (error) {
                console.error('Work readiness export failed:', error);
                this.$swal.fire(
                    'Error',
                    error && error.message ? error.message : 'Failed to export report.',
                    'error'
                );
            } finally {
                this.isExportExcel = false;
                this.isApiCallRecurisive = false;
            }
        },
        exportToExcel(reportName) {
            var XLSX = xlsx;
            var workbook = XLSX.utils.book_new();

            if (this.generatedReport.length > 0) {
                var submissionsSheet = XLSX.utils.json_to_sheet(this.generatedReport);
                submissionsSheet['!cols'] = [
                    { width: 8 },
                    { width: 28 },
                    { width: 28 },
                    { width: 16 },
                    { width: 12 },
                    { width: 20 },
                    { width: 30 },
                    { width: 35 },
                    { width: 35 },
                    { width: 14 },
                    { width: 14 },
                    { width: 16 },
                    { width: 16 },
                ];
                XLSX.utils.book_append_sheet(workbook, submissionsSheet, 'Submissions');
            }

            if (this.generatedLinkTrackReport.length > 0) {
                var linkTrackSheet = XLSX.utils.json_to_sheet(this.generatedLinkTrackReport);
                linkTrackSheet['!cols'] = [
                    { width: 8 },
                    { width: 24 },
                    { width: 28 },
                    { width: 22 },
                    { width: 22 },
                ];
                XLSX.utils.book_append_sheet(workbook, linkTrackSheet, 'External Link Tracking');
            }

            XLSX.writeFile(workbook, reportName + '.xlsx');
            this.generatedReport = [];
            this.generatedLinkTrackReport = [];
        },
    },
};
</script>

<style scoped>
.wrp-note-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.625rem;
    max-width: 280px;
}

.wrp-note-preview {
    width: 100%;
    padding: 0.625rem 0.75rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 0.625rem;
    border-left: 3px solid #6366f1;
}

.wrp-note-text {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.45;
    color: #475569;
    white-space: pre-wrap;
    word-break: break-word;
}

.wrp-note-empty {
    margin: 0;
    font-size: 0.8125rem;
    font-style: italic;
    color: #94a3b8;
}

.wrp-chat-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    border: none;
    border-radius: 0.625rem;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.3);
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.wrp-chat-btn:hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
    transform: translateY(-1px);
}

.wrp-chat-btn-icon-wrap {
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.375rem;
    background: rgba(255, 255, 255, 0.2);
}

.wrp-chat-btn-icon {
    font-size: 0.75rem;
}

.wrp-chat-btn-label {
    letter-spacing: 0.01em;
}

/* Modal — aligned with master-contact-list Add New Contact */
.wrp-modal-header {
    padding: 1.25rem 3rem 1.25rem 1.5rem;
    background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #2563eb 100%);
    color: #fff;
    border-radius: 1rem 1rem 0 0;
    box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.08);
}

.wrp-modal-header-inner {
    display: flex;
    align-items: center;
    gap: 0.875rem;
}

.wrp-modal-header-icon-wrap {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.75rem;
    background: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
}

.wrp-modal-header-icon {
    font-size: 1.35rem;
    opacity: 0.95;
}

.wrp-modal-header-title {
    font-size: 1.125rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0;
    line-height: 1.3;
}

.wrp-modal-header-sub {
    margin: 0.2rem 0 0 0;
    font-size: 0.8125rem;
    color: rgba(219, 234, 254, 0.95);
    line-height: 1.35;
}

.wrp-modal-header-email {
    opacity: 0.85;
}

.wrp-modal-body {
    padding: 1.5rem;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.wrp-modal-card {
    background: #fff;
    border-radius: 1rem;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 40px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.04);
    overflow: hidden;
}

.wrp-modal-hint {
    margin: 1.25rem 0 0 0;
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
    color: #64748b;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    line-height: 1.45;
}

.wrp-modal-hint-icon {
    margin-right: 0.25rem;
    color: #f59e0b;
}

.wrp-fade-enter-active,
.wrp-fade-leave-active {
    transition: opacity 0.2s ease;
}

.wrp-fade-enter,
.wrp-fade-leave-to {
    opacity: 0;
}

.wrp-export-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    border: none;
    border-radius: 0.625rem;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(5, 150, 105, 0.25);
    transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.wrp-export-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #047857 0%, #065f46 100%);
    box-shadow: 0 6px 20px rgba(5, 150, 105, 0.35);
    transform: translateY(-1px);
}

.wrp-export-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.wrp-export-btn-icon {
    font-size: 0.875rem;
}
</style>